// chartZoom.js — pure, DOM-agnostic zoom+pan math for the interactive chart
// (Tier-1 accessibility: explicit graph magnification).
//
// The chart is magnified with a CSS `transform: translate() scale()` on a
// wrapper element that sits inside the existing `overflow:hidden` chart
// container. Because a CSS transform does NOT create scrollable overflow, pan
// is modelled explicitly here as a clamped translate (driven by keyboard arrows
// and pointer drag in the component). Keeping the transform inside an
// already-clipped container means magnified content can never break page
// layout ("越界"), and reset() always restores the identity view ("回到原位").
//
// This module owns only the {scale, tx, ty} state and the tiny string/keyboard
// helpers around it, so it is fully unit-testable with no DOM. No logic is
// extracted from InteractiveGraph.js — all new.

export const MIN_ZOOM = 1; // never below "fit to container" (no sub-fit whitespace)
export const MAX_ZOOM = 3;
export const ZOOM_STEP = 0.25;
export const PAN_STEP = 48; // px per arrow-key / drag step

export const IDENTITY = Object.freeze({ scale: 1, tx: 0, ty: 0 });

const EPSILON = 1e-6;

function round2(n) {
  return Math.round(n * 100) / 100;
}

function finite(n, fallback) {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

// Clamp any input to [MIN_ZOOM, MAX_ZOOM]; non-finite → MIN, +Infinity → MAX,
// so a bad value can never reach a CSS transform as NaN.
export function clampScale(scale) {
  const n = Number(scale);
  if (!Number.isFinite(n)) return n === Infinity ? MAX_ZOOM : MIN_ZOOM;
  if (n < MIN_ZOOM) return MIN_ZOOM;
  if (n > MAX_ZOOM) return MAX_ZOOM;
  return n;
}

// With a centered transform, a chart scaled by S overflows its container by
// (S-1)*dimension total — half on each side — so translate is pannable within
// ±((S-1)/2)*dimension. At/below fit there is nothing to pan.
export function computeMaxTranslate(scale, dimension) {
  const s = clampScale(scale);
  const dim = finite(dimension, 0);
  if (s <= MIN_ZOOM || dim <= 0) return 0;
  return ((s - 1) / 2) * dim;
}

function clampToRange(value, limit) {
  const v = finite(value, 0);
  let out = v;
  if (v > limit) out = limit;
  else if (v < -limit) out = -limit;
  // Normalise -0 → 0 so callers/tests see a single canonical zero.
  return out === 0 ? 0 : out;
}

// Normalise a {scale, tx, ty} against a container box: clamp the scale, then
// re-clamp translate to whatever is reachable at that scale (so zooming out
// pulls the view back into range automatically).
export function clampState(state, dims) {
  const scale = clampScale(state && state.scale);
  const maxX = computeMaxTranslate(scale, dims && dims.width);
  const maxY = computeMaxTranslate(scale, dims && dims.height);
  return {
    scale,
    tx: clampToRange(state && state.tx, maxX),
    ty: clampToRange(state && state.ty, maxY),
  };
}

export function zoomIn(state, dims) {
  const scale = round2(clampScale(state && state.scale) + ZOOM_STEP);
  return clampState({ ...state, scale }, dims);
}

export function zoomOut(state, dims) {
  const scale = round2(clampScale(state && state.scale) - ZOOM_STEP);
  return clampState({ ...state, scale }, dims);
}

export function pan(state, dx, dy, dims) {
  const tx = finite(state && state.tx, 0) + finite(dx, 0);
  const ty = finite(state && state.ty, 0) + finite(dy, 0);
  return clampState({ scale: state && state.scale, tx, ty }, dims);
}

// A pointer drag/resize delta is measured in client (screen) pixels, but chart
// elements positioned in the chart's own (SVG-layout) coordinates live inside a
// `scale(S)` wrapper — so a raw client delta moves/resizes them S× too far when
// magnified (F4). Convert a client-space delta to local coordinates by dividing
// by the active scale. At fit (S=1) this is an exact no-op, so the normal
// unmagnified drag path is unchanged (zero regression). clampScale keeps S in
// [MIN,MAX] and maps non-finite → MIN, so this never divides by 0 or NaN.
export function toLocalDelta(dx, dy, state) {
  const s = clampScale(state && state.scale);
  return { dx: finite(dx, 0) / s, dy: finite(dy, 0) / s };
}

export function reset() {
  return { scale: MIN_ZOOM, tx: 0, ty: 0 };
}

export function canZoomIn(state) {
  return clampScale(state && state.scale) < MAX_ZOOM - EPSILON;
}

export function canZoomOut(state) {
  return clampScale(state && state.scale) > MIN_ZOOM + EPSILON;
}

// True once magnified beyond fit (drives the pan affordance + "is zoomed" UI).
export function isZoomed(state) {
  return clampScale(state && state.scale) > MIN_ZOOM + EPSILON;
}

export function toTransform(state) {
  const scale = clampScale(state && state.scale);
  const tx = finite(state && state.tx, 0);
  const ty = finite(state && state.ty, 0);
  return `translate(${tx}px, ${ty}px) scale(${scale})`;
}

export function formatZoomPercent(scale) {
  return `${Math.round(clampScale(scale) * 100)}%`;
}

// DOM helper (the only non-pure export): true if a pointerdown target sits on,
// or inside, an existing draggable chart element, so a zoom-pan does NOT also
// drag the whole chart when the user grabs one while magnified (Codex P2).
// Detects two EXPLICIT, non-d3-filtered markers: the `ig-draggable` class (set
// on the d3.drag pie/Venn labels) and the unconditional inline
// `touch-action:none` the legend move/resize handles already set. (D3's own
// touch-action is unreliable on non-touch desktops — it only applies to
// touch-filtered targets — which is why an explicit class is used for the
// d3.drag labels; Codex R3.) Walks up to — but excluding — the boundary (the
// zoom wrapper itself, whose own touch-action must be ignored).
export function pointerStartsOnDraggable(target, boundary) {
  let node = target;
  while (node && node !== boundary) {
    if (node.classList && node.classList.contains('ig-draggable')) return true;
    if (node.style && node.style.touchAction === 'none') return true;
    node = node.parentNode;
  }
  return false;
}

// Map a KeyboardEvent.key to a zoom/pan action, or null. Enter/Tab/Space and
// any other key return null so activation, focus traversal and unrelated
// shortcuts are never swallowed by the chart's key handler.
export function resolveZoomKey(key) {
  switch (key) {
    case '+':
    case '=':
      return 'in';
    case '-':
    case '_':
      return 'out';
    case '0':
      return 'reset';
    case 'ArrowLeft':
      return 'panLeft';
    case 'ArrowRight':
      return 'panRight';
    case 'ArrowUp':
      return 'panUp';
    case 'ArrowDown':
      return 'panDown';
    default:
      return null;
  }
}
